// Draft copy + data for the public landing page.
//
// DRAFT — pending Matt's approval before launch (external-content rule in
// CLAUDE.md). Everything a non-developer would want to edit lives here, in one
// place, following references/voice.md: short sentences, warm, direct, no em
// dashes. Real client names + USD figures are cleared for public use
// (PRD 2026-07-18); each carries a one-line context so the figures read
// honestly to a UK audience.

// Testimonials now live as hybrid result cards (a client quote + metric on the
// same card — see the `quote` fields in `results` below and ResultCard). The
// standalone carousel flag + arrays below are unused by the live page, kept only
// for the design-system showcase / a future dedicated section.
export const SHOW_TESTIMONIALS = false

export const site = {
  name: "Matthew Traynor",
  role: "Freelance Google Ads Consultant",
  email: "mftraynor@gmail.com",
  linkedinUrl: "https://www.linkedin.com/in/matthew-traynor100/",
}

// Pain-first opener. The headline is Matt's own line (it opens his Upwork
// overview): it names the client's problem rather than leading with a
// credential. The trust row deliberately drops "years" — against competitors
// running 15 to 18, duration is the weakest axis, so it leads on managed spend
// and client count instead, and carries Microsoft Ads as the differentiator.
export const hero = {
  eyebrow: "Independent Google Ads specialist",
  title: "Most accounts aren't broken. They're leaking.",
  subhead:
    "I'm Matt. I find where the budget is quietly draining away, fix it, and turn spend into leads and sales that actually convert. Google and Microsoft Ads, hands-on, direct with me.",
  ctaLabel: "Apply to work together",
  trust: [
    "$5M+ in ad spend managed",
    "60+ businesses",
    "Google and Microsoft Ads",
    "Retainers, no lock-in",
  ],
}

export interface Result {
  client: string
  context?: string
  figure: string
  figureLabel: string
  // Third-person description shown on numbers-only cards. Omit when the card
  // carries a client `quote` instead (the quote replaces it).
  detail?: string
  // Short, truthful metric category shown as a teal tag (e.g. "Conversion rate").
  tag?: string
  // Client quote + attribution. When set, the card renders as a hybrid
  // result + testimonial (metric badge AND the client's own voice). Consent to
  // name each person was given. Headshots live in assets/testimonials.
  quote?: string
  quoteName?: string
  quoteRole?: string
  // Stub card for an account whose case details aren't written yet. Placeholder
  // cards render ONLY in development (see Results.tsx) so a blank card can
  // never ship to production. To publish one: fill in the real figure/label/
  // detail (and optional context/tag) and DELETE this `placeholder` line.
  placeholder?: boolean
}

export const results: Result[] = [
  {
    client: "Merchant Automotive",
    context: "Vehicle transmission parts",
    tag: "Return on ad spend",
    figure: "$1.15M",
    figureLabel: "conversion value at 19.5x ROAS",
    detail:
      "Rebuilt the product feed and conversion tracking. Conversion value grew 107% to $1.15M at a 19.5x return on ad spend.",
  },
  {
    client: "Schorr Law",
    context: "California Real Estate law firm",
    tag: "Conversion rate",
    figure: "7.5% to 35%",
    figureLabel: "conversion rate, at $41 CPL",
    detail:
      "Rebuilt the account on buyer intent. Conversion rate climbed from 7.5% to 35%, and cost per lead landed at $41.",
  },
  {
    client: "Elec Direct",
    context: "B2B Electronics Supplier",
    tag: "Revenue",
    figure: "+$230k",
    figureLabel: "in added revenue",
    detail:
      "On a $50k/month account, a targeted $20k increase on the core 'Lugs' line added $230k in revenue, backed by enhanced conversion tracking.",
  },

  {
    client: "Ace Canine",
    context: "Canine health supplements",
    tag: "Return on ad spend",
    figure: "11:1",
    figureLabel: "ROAS held for two years",
    detail:
      "A crowded market full of big players. I sharpened the copy to emphasise the superior quality of our products over competitors'. The result was an 11:1 return on ad spend across two years.",
  },
  {
    client: "Epsilon Tech",
    context: "Materials-testing extensometers",
    tag: "Cost per lead",
    figure: "$250",
    figureLabel: "cost per lead on $10k+ orders",
    detail:
      "Orders start at $10,000 and run much higher, so a $250 cost per lead pays for itself many times over on this niche, technical product.",
  },
  {
    client: "HiTech Wireless",
    context: "Two-way radio supplier",
    tag: "Return on ad spend",
    figure: "8:1",
    figureLabel: "ROAS held over two years",
    detail:
      "Held an 8:1 return on ad spend across two years, while launching many new product lines in the account.",
  },
  {
    client: "Nexthermal",
    context: "Industrial heating elements",
    tag: "Cost per lead",
    figure: "$150",
    figureLabel: "cost per lead on million-dollar deals",
    detail:
      "Timed the campaigns to their product launches and made the USP clear over competitors. A $150 cost per lead on deals that run into the millions.",
  },
  {
    client: "Bissell Woodworking",
    context: "Handmade wood furniture",
    tag: "Cost per lead",
    figure: "$40",
    figureLabel: "cost per lead on made-to-order furniture",
    detail:
      "A $40 cost per lead on handmade, made-to-order furniture. Their YouTube campaign added reach on top, pulling 20,000 views a month.",
  },
  {
    client: "Climb Vertex",
    context: "Rock climbing gym",
    tag: "Camp sign-ups",
    figure: "98%",
    figureLabel: "of summer camp places filled",
    detail:
      "Launched their summer climbing camp to 98% full, and achieved 10,000 views a month in their YouTube campaign.",
  },
  {
    client: "Barton Door",
    context: "Overhead door specialists",
    tag: "Cost per lead",
    figure: "$40",
    figureLabel: "cost per lead on six-figure deals",
    detail:
      "A $40 cost per lead across residential and commercial, including commercial doors worth hundreds of thousands in deal value.",
  },
  {
    client: "Golf International",
    context: "Golf tours and travel",
    tag: "Cost per lead",
    figure: "$250",
    figureLabel: "cost per lead on $100k trips",
    detail:
      "A $250 cost per lead on premium golf trips worth $100,000 each.",
  },
]

// UK client voices — shown in their own Testimonials section under the hero
// (see Testimonials.tsx), NOT in the results wall. Hybrid cards: a metric badge
// AND the client's own quote. Named quotes with consent; metric only (monthly
// spend kept private). Reuses the Result shape for the shared ResultCard.
export const clientVoices: Result[] = [
  {
    client: "NOVA",
    context: "Software for solar installers",
    tag: "Traffic quality",
    figure: "94%",
    figureLabel: "fewer irrelevant queries",
    quote:
      "Matt showed us that a lot of our spend was going to searches that would never convert. He then cut our irrelevant queries by 94%. We're now spending money on only people who actually want what we do and our lead quality is much better.",
    quoteName: "Chris Orr",
    quoteRole: "CEO, NOVA",
  },
  {
    client: "Warm Homes",
    context: "UK home energy upgrades",
    tag: "Cost per lead",
    figure: "£17",
    figureLabel: "cost per lead",
    quote:
      "Our cost per lead was £38 and Matt brought it down to £17. We're booking more homeowners than ever.",
    quoteName: "Sean Daly",
    quoteRole: "CEO, Warm Homes",
  },
  {
    client: "SolarHero",
    context: "UK residential solar",
    tag: "Cost per lead",
    figure: "£34",
    figureLabel: "cost per lead",
    quote:
      "After Google changed things our leads dropped right off and I couldn't work out why. Matt turned it around fast, and we're in a better spot than we were before. We're getting quality solar leads every day now. I couldn't recommend him more.",
    quoteName: "Yousaf Bilal",
    quoteRole: "Marketing Director, SolarHero",
  },
]

export interface Tier {
  name: string
  includes: string[]
  ctaLabel: string
  // Analytics id for the card's CTA. Held on the tier rather than derived from
  // `featured`, so re-ordering or re-featuring a card can't silently relabel
  // the click event.
  trackId: string
  featured?: boolean
  // Optional pill shown beside the tier name (e.g. "Start here").
  badge?: string
  // A short note set apart under the includes list. Not a feature: it frames
  // what the offer is actually for.
  note?: string
  // Optional link to a worked example of the deliverable. Served as a static
  // file from public/, so it's a plain <a>, not an Inertia <Link>.
  sample?: { href: string; label: string; trackId: string }
  // Fixed price shown on the card (e.g. "£199"). Omit for price-on-application.
  price?: string
}

export const offers = {
  eyebrow: "How we can work",
  title: "Two ways in.",
}

// The audit is the featured card on purpose: it's the entry product, and
// management is the back end. It also happens to be the position nobody else
// in this market claims.
export const tiers: Tier[] = [
  {
    name: "The Blind-Spot Audit",
    includes: [
      "Full account and conversion-tracking teardown",
      "Where the budget is being wasted, in plain English",
      "A prioritised fix list you can action with or without me",
      "A walkthrough call, not just a PDF",
    ],
    // Covers all three shapes the audit takes without naming them: opportunity
    // only (no account access needed), execution only (account access), or both.
    // Worded so a business not yet running ads doesn't read itself out of it.
    note:
      "I run this whether you're already spending or still weighing it up. If you're not running yet, it sizes the opportunity. If you are, it shows how hard the budget is working and where it is leaking.",
    sample: {
      href: "/sample-audit",
      label: "See a full sample audit",
      trackId: "sample_audit",
    },
    ctaLabel: "Apply for an audit",
    trackId: "offer_audit",
    badge: "Start here",
    featured: true,
    price: "£199",
  },
  {
    name: "Ongoing Management",
    includes: [
      "Hands-on account management, direct with me",
      "Proper tracking and reporting you actually understand",
      "Continuous testing on keywords, ads, and landing pages",
      "No long contracts, no lock-in",
    ],
    ctaLabel: "Apply for management",
    trackId: "offer_management",
  },
]

// Application form options.
export const spendBands = [
  "Not running ads yet",
  "Under £1k / month",
  "£1k-3k / month",
  "£3k-10k / month",
  "£10k+ / month",
]

export const interestOptions = ["Audit", "Ongoing management", "Not sure yet"]

export const form = {
  eyebrow: "Apply",
  title: "Tell me about your account.",
  lead: "A few quick questions so I know it's a fit before we talk.",
  privacyNote: "I use your details only to reply to your enquiry. I never share them.",
  submitLabel: "Send application",
}

export interface Testimonial {
  quote: string
  name: string
  role: string
  // Optional headshot URL / asset path. Falls back to an initials monogram.
  profileImage?: string
}

// Empty until real quotes exist. The section stays hidden while this is empty
// and SHOW_TESTIMONIALS is false.
export const testimonials: Testimonial[] = []

// DEV-ONLY FILLER. Lets the testimonials carousel be previewed before real quotes
// exist. Shown only when import.meta.env.DEV is true (see Testimonials.tsx) and
// NOT gated by SHOW_TESTIMONIALS, so it can never reach a production build — no
// placeholder quote goes live. Images are intentionally blank (no profileImage);
// swap in real headshots + move to `testimonials` when ready.
export const fillerTestimonials: Testimonial[] = [
  {
    quote:
      "Placeholder testimonial. Matt found budget we'd been wasting for months and turned it into leads fast.",
    name: "Placeholder Name",
    role: "Founder, Example Co",
  },
  {
    quote:
      "Placeholder testimonial. Straight answers, no jargon, and a cost per lead I can finally understand.",
    name: "Placeholder Name",
    role: "Marketing Lead, Example Ltd",
  },
  {
    quote:
      "Placeholder testimonial. A specialist owning the account made a real difference to our pipeline.",
    name: "Placeholder Name",
    role: "Director, Example Group",
  },
  {
    quote:
      "Placeholder testimonial. Clear reporting I can act on and testing that never stops.",
    name: "Placeholder Name",
    role: "Owner, Example Trades",
  },
  {
    quote:
      "Placeholder testimonial. Spend finally works as hard as we do. Leads are up and steady.",
    name: "Placeholder Name",
    role: "CEO, Example Services",
  },
]
